/**
 * VibeTime CLI - Direct ccusage proxy for full compatibility
 * This provides complete ccusage compatibility while adding sync functionality
 */

import { spawn } from 'node:child_process'

/**
 * Execute ccusage command directly with all arguments passed through
 */
export async function executeCcusage(args: string[]): Promise<{ stdout: string; stderr: string; exitCode: number }> {
  return new Promise((resolve) => {
    // Try to use npx ccusage from the installed npm package
    console.log(`🔍 Executing ccusage: npx ccusage ${args.join(' ')}`)
    
    const child = spawn('npx', ['ccusage', ...args], {
      stdio: ['inherit', 'pipe', 'pipe'],
      shell: false
    })

    let stdout = ''
    let stderr = ''

    child.stdout.on('data', (data) => {
      const output = data.toString()
      stdout += output
      // Pass through to console for real-time display
      process.stdout.write(output)
    })

    child.stderr.on('data', (data) => {
      const output = data.toString()
      stderr += output
      // Pass through to console for real-time display
      process.stderr.write(output)
    })

    child.on('close', (code) => {
      resolve({
        stdout,
        stderr,
        exitCode: code || 0
      })
    })

    child.on('error', (error) => {
      resolve({
        stdout: '',
        stderr: error.message,
        exitCode: 1
      })
    })
  })
}

/**
 * Get ccusage data in JSON format for processing
 */
export async function getCcusageData(command: 'daily' | 'monthly' | 'session' | 'blocks', options: string[] = []): Promise<any> {
  return new Promise((resolve, reject) => {
    const args = [command, '--json', ...options]
    
    // Run ccusage via npx
    const child = spawn('npx', ['ccusage', ...args], {
      stdio: ['inherit', 'pipe', 'pipe'],
      shell: false
    })

    let stdout = ''
    let stderr = ''

    child.stdout.on('data', (data) => {
      stdout += data.toString()
    })

    child.stderr.on('data', (data) => {
      stderr += data.toString()
    })

    child.on('close', (code) => {
      if (code !== 0) {
        reject(new Error(`ccusage failed with code ${code}: ${stderr}`))
        return
      }
      
      try {
        // Find JSON in the output (it might have extra text from bun)
        const lines = stdout.split('\n')
        let jsonStr = ''
        let foundJson = false
        
        for (const line of lines) {
          if (line.trim().startsWith('{')) {
            foundJson = true
          }
          if (foundJson) {
            jsonStr += line + '\n'
          }
        }
        
        if (!jsonStr.trim()) {
          // No JSON found, might be an error message
          reject(new Error(`No JSON output from ccusage: ${stdout}`))
          return
        }
        
        const parsed = JSON.parse(jsonStr.trim())
        resolve(parsed)
      } catch (error) {
        reject(new Error(`Failed to parse ccusage JSON output: ${error}\nOutput: ${stdout}`))
      }
    })

    child.on('error', (error) => {
      reject(error)
    })
  })
}

// Re-export for compatibility
export { getCcusageData as daily, getCcusageData as monthly, getCcusageData as session, getCcusageData as blocks }